import {
  Snackbar,
  Alert,
  Card,
  CircularProgress,
  Backdrop,
  Icon,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
} from "@mui/material";

import { useEffect, useState } from "react";

import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";
import usePageTitle from "app/pageTitle";

import MDInput from "theme/components/MDInput";
import MDButton from "theme/components/MDButton";
import DataTable from "theme/Tables/DataTable";
import axios from "axios.js";
import { useAwaitDialog } from "app/components/status_dialog";

import BoundaryMap from "app/components/boundary_mapping";

function BoundariesPage() {
  const pageTitle = "Boundaries";
  usePageTitle(pageTitle);

  const statusDialog = useAwaitDialog();

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");

  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  const [refreshPage, setRefreshPage] = useState(false);
  const [pageLoaded, setPageLoaded] = useState(false);

  const [boundaries, setBoundaries] = useState([]);

  const [title, setTitle] = useState(null);
  const [coordinates, setCoordinates] = useState([]);

  const [openCreateDialog, setOpenCreateDialog] = useState(false);

  async function createBoundary() {
    if (title === null || title.trim() === "") {
      setSeverity("error");
      setErrMsg("Boundary name is required.");
      setOpen(true);

      return;
    }

    if (!coordinates || coordinates.length < 3) {
      setSeverity("error");
      setErrMsg("At least 3 points are required.");
      setOpen(true);

      return;
    }

    setOpenCreateDialog(false);
    setShowLoading(true);

    const json = {
      title: `${title}`,
      coords: coordinates,
    };

    await axios
      .post("portal/territories/boundaries/create.json", json)
      .then(async (result) => {
        setShowLoading(false);

        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          await statusDialog.show(result.data.data.message, "success", "Success!");

          setTitle(null);
          setCoordinates([]);

          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");

        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );

        setOpen(true);
      });
  }

  async function deleteBoundary(ref) {
    if (!window.confirm("Delete this boundary?")) {
      return;
    }

    setShowLoading(true);

    await axios
      .get(`/portal/territories/boundaries/${ref}/delete.json`)
      .then((result) => {
        setShowLoading(false);

        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");

        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );

        setOpen(true);
      });
  }

  const boundaryTableColumns = [
    {
      Header: "Name",
      accessor: "name",
      align: "left",
    },
    {
      Header: "Actions",
      accessor: "actions",
      align: "right",
    },
  ];

  const buildRows = (data) => {
    const rows = [];

    data.map((row) => {
      rows.push({
        name: row.title,

        actions: (
          <MDBox mt={1} variant="button" display="flex" justifyContent="flex-end" gap={0.5}>
            <Tooltip title="Delete Boundary">
              <MDButton
                size="small"
                variant="outlined"
                color="error"
                iconOnly
                onClick={() => {
                  deleteBoundary(row.reference);
                }}
              >
                <Icon sx={{ cursor: "pointer" }} fontSize="small">
                  delete
                </Icon>
              </MDButton>
            </Tooltip>
          </MDBox>
        ),
      });

      return null;
    });

    return rows;
  };

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);

      await axios
        .get(`/portal/territories/boundaries.json`)
        .then((result) => {
          setShowLoading(false);

          if (result.data.status === "success") {
            const rows = buildRows(result.data.data.boundaries);

            setBoundaries(rows);
            setPageLoaded(true);
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");

          setErrMsg(
            `Unable to complete request. ${
              navigator.onLine ? "" : "Make sure you have an active internet connection"
            }`
          );

          setOpen(true);
        });
    };

    fetchData();
  }, [refreshPage]);

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>

      <Dialog open={openCreateDialog} fullWidth maxWidth="md">
        <DialogTitle>
          <MDBox>New Boundary</MDBox>
        </DialogTitle>

        <DialogContent>
          <MDBox mt={0} mb={3}>
            <MDBox mb={0}>
              <MDInput
                value={title || ""}
                name="title"
                margin="dense"
                label="Boundary Name"
                type="text"
                fullWidth
                variant="outlined"
                onChange={(e) => setTitle(e.target.value)}
              />
            </MDBox>

            <BoundaryMap coordinates={coordinates} onChange={setCoordinates} />
          </MDBox>
        </DialogContent>

        <DialogActions>
          <MDButton
            onClick={() => {
              setOpenCreateDialog(false);
              setTitle(null);
              setCoordinates([]);
            }}
            color="dark"
            variant="outlined"
          >
            Cancel
          </MDButton>

          <MDButton
            color="dark"
            variant="contained"
            onClick={() => {
              createBoundary();
            }}
          >
            Create
          </MDButton>
        </DialogActions>
      </Dialog>

      <MDBox pt={1} pb={3}>
        {pageLoaded && (
          <MDBox>
            <Card
              sx={{
                height: "100%",
                marginTop: "0px",
              }}
            >
              <MDBox p={2}>
                <MDBox display="flex" justifyContent="space-between" alignItems="center">
                  <MDBox>
                    <MDTypography variant="h6" fontWeight="medium">
                      Boundaries
                    </MDTypography>

                    <MDTypography variant="button" color="text" fontWeight="light">
                      Create and manage your custom geographic boundaries.
                    </MDTypography>
                  </MDBox>

                  <MDButton
                    variant="contained"
                    color="dark"
                    onClick={() => {
                      setTitle(null);
                      setCoordinates([]);
                      setOpenCreateDialog(true);
                    }}
                    startIcon={<Icon>add</Icon>}
                  >
                    Create Boundary
                  </MDButton>
                </MDBox>
              </MDBox>

              {boundaries.length > 0 && (
                <MDBox mt={0}>
                  <DataTable
                    table={{
                      columns: boundaryTableColumns,
                      rows: boundaries,
                    }}
                    isSorted
                    canSearch={false}
                    entriesPerPage={false}
                    showTotalEntries
                    buttons
                    noEndBorder
                  />
                </MDBox>
              )}
            </Card>
          </MDBox>
        )}
      </MDBox>

      <Footer />
    </DashboardLayout>
  );
}

export default BoundariesPage;